/**
 * sl-yphr: ELSTER Canvas Background Component
 *
 * Follows Note-Edgeless-Background pattern for correct positioning.
 * EXACT COPY of AutomationCanvasBackground pattern for Frame Preview support.
 */

import { DefaultTheme } from '@blocksuite/affine-model';
import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { stopPropagation } from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  type BlockStdScope,
  PropTypes,
  requiredProperties,
  ShadowlessElement,
  stdContext,
} from '@blocksuite/std';
import { consume } from '@lit/context';
import { computed, effect } from '@preact/signals-core';
import { nothing } from 'lit';
import { property } from 'lit/decorators.js';

import type { ElsterBlockModel } from '../../schemas/index.js';
import { elsterBackground } from '../elster-canvas-block.css.js';

@requiredProperties({
  elster: PropTypes.instanceOf(Object),
})
export class ElsterCanvasBackground extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  readonly backgroundStyle$ = computed(() => {
    const themeProvider = this.std.get(ThemeProvider);
    const theme = themeProvider.theme$.value;

    const backgroundColor = themeProvider.generateColorProperty(
      '--streamlain-background-primary-color',
      DefaultTheme.noteBackgrounColor,
      theme
    );

    // Use fixed default values like Database (no dynamic props access)
    const borderRadius = 8;
    const borderSize = 1;

    return {
      borderRadius: borderRadius + 'px',
      backgroundColor: backgroundColor,
      borderWidth: `${borderSize}px`,
      borderStyle: 'solid',
      boxShadow: 'var(--streamlain-shadow-1)',
    };
  });

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(elsterBackground);
    this.disposables.add(
      effect(() => {
        Object.assign(this.style, this.backgroundStyle$.value);
      })
    );
    this.disposables.addFromEvent(this, 'pointerdown', stopPropagation);
  }

  override render() {
    return nothing;
  }

  @consume({ context: stdContext })
  accessor std!: BlockStdScope;

  @property({ attribute: false })
  accessor editing: boolean = false;

  @property({ attribute: false })
  accessor elster!: ElsterBlockModel;
}

declare global {
  interface HTMLElementTagNameMap {
    'elster-canvas-background': ElsterCanvasBackground;
  }
}
